/**
 * Pure decision helper for the network-recovery upload nudge (bug 2026-08-05).
 *
 * When tus is asleep in its exponential retry backoff (up to 240s per slot),
 * it doesn't notice that the network came back until the sleep expires.
 * Perceived lag on a poor→good network switch is 1–2 min. The nudge fires
 * tus.abort(false)+start() on every in-flight session, immediately cutting
 * short the sleep and firing a fresh PATCH.
 *
 * This helper decides whether a given network transition warrants a nudge.
 * Fires ONLY on improvements — offline→online, or effectiveType climbing
 * the slow-2g/2g/3g/4g ladder. Degradations and no-op transitions are
 * ignored so we don't kill an active PATCH for a flapping event.
 *
 * The first observation returns false — at boot we don't know if we just
 * came online or were online all along, so skipping is safer than
 * spuriously aborting a healthy first PATCH.
 *
 * @param {{ online?: boolean, effectiveType?: string } | null | undefined} prev
 * @param {{ online?: boolean, effectiveType?: string } | null | undefined} curr
 * @returns {boolean}
 */
export const shouldTriggerRecoveryNudge = (prev, curr) => {
  if (!curr) return false;
  if (curr.online === false) return false;
  if (!prev) return false;

  // Offline → online is the clearest recovery signal.
  if (prev.online === false && curr.online !== false) return true;

  // effectiveType climb — treat unknown as the lowest rung so a
  // navigator.connection that populates late still counts as "improvement".
  const rankOf = (effectiveType) => {
    switch (effectiveType) {
      case "slow-2g": return 1;
      case "2g": return 2;
      case "3g": return 3;
      case "4g": return 4;
      default: return 0; // undefined / null / unknown
    }
  };
  return rankOf(curr.effectiveType) > rankOf(prev.effectiveType);
};
